import { Box, Flex, Stack, Text } from '@mantine/core';
import { IconCircleCheckFilled } from '@tabler/icons-react';
import IntegrationLogo from '@/pages/Integrations/components/IntegrationLogo.tsx';
import useRetrieveLinkedDatasets from '@/hooks/api/datasets/linked/useRetrieveLinkedDatasets.ts';
import CustomLoadingOverlay from '@/components/CustomLoadingOverlay.tsx';
import FilePickerButton from '@/components/IntegrationFilePicker/FilePickerButton.tsx';
import useDeleteLinkedDataset from '@/hooks/api/datasets/linked/useDeleteLinkedDataset.ts';
import { useMemo, useState } from 'react';
import ConfirmationModal from '@/components/ConfirmationModal.tsx';
import LinkedDatasetBadge from '@/pages/Editor/components/StepSidebar/DataSources/LinkedDatasetBadge.tsx';
import { IntegrationProvider } from '@/types/integrations.ts';
import { LinkedDataset } from '@/types/datasets.ts';
import useRetrieveLatestAutomationVersion from '@/hooks/api/automationVersions/useRetrieveLatestAutomationVersion.ts';
import { collectDeleteImpacts } from '@/utils/deleteImpacts.ts';
import DeleteImpactWarning from '@/components/DeleteImpactWarning.tsx';
import {
  getFullTenantLabel,
  getIntegrationInstancePrimaryLabel,
} from '@/utils/integrationDisplay.ts';

type props = {
  provider: IntegrationProvider;
};

const ConnectedIntegration = ({ provider }: props) => {
  const {
    data: linkedDatasets,
    isPending: loadingLinkedDatasets,
    isError,
  } = useRetrieveLinkedDatasets();

  const [datasetToDelete, setDatasetToDelete] = useState<undefined | LinkedDataset>(undefined);

  const { mutate: deleteDataset } = useDeleteLinkedDataset({});
  const { data: latestVersion } = useRetrieveLatestAutomationVersion();

  const deleteImpacts = useMemo(
    () =>
      datasetToDelete
        ? collectDeleteImpacts(latestVersion?.steps, {
            type: 'linked_dataset',
            linkedDatasetId: datasetToDelete.id,
          })
        : [],
    [datasetToDelete, latestVersion?.steps],
  );

  return (
    <>
      {provider.instances.map((instance) => {
        const tenantLabel = getFullTenantLabel(instance.sharepoint_tenant_id);
        const connectionLabel = getIntegrationInstancePrimaryLabel(provider, instance);

        return (
          <Stack key={instance.id} gap={4}>
            <Box
              px="sm"
              py={'xs'}
              style={{ border: '1px solid #e0e0e0', borderRadius: 4 }}
              data-testid={`connected-integration-instance-${instance.id}`}
            >
              <Flex justify="space-between" align="center" mb="md">
                <Flex gap="xs" align="center" style={{ minWidth: 0 }}>
                  <IntegrationLogo name={provider.name} />

                  <Text size="sm" fw={500}>
                    {provider.display_name}
                  </Text>
                  <Text size={'sm'} truncate={'end'} style={{ wordBreak: 'break-all' }}>
                    ({connectionLabel})
                  </Text>
                </Flex>

                <Flex gap={6} align="center" ml={6}>
                  <IconCircleCheckFilled size={13} color="green" />
                  <Text size="xs" c="#60BB82">
                    Connected
                  </Text>
                </Flex>
              </Flex>
              {tenantLabel && (
                <Text size="xs" c="#757575" mb="sm">
                  {tenantLabel}
                </Text>
              )}

              {/* Selected folder tags */}
              <Box pos="relative">
                <CustomLoadingOverlay visible={loadingLinkedDatasets} />
                {isError && (
                  <Text size="xs" c="red" role="alert" mb="xs">
                    Linked data sources could not be loaded. Refresh to try again.
                  </Text>
                )}
                <Flex wrap="wrap" gap="xs" align={'center'}>
                  {linkedDatasets?.results
                    .filter((l) => l.integration.id === instance.id)
                    .map((dataset) => (
                      <LinkedDatasetBadge
                        linkedDataset={dataset}
                        setDatasetToDelete={setDatasetToDelete}
                        key={dataset.id}
                      />
                    ))}
                  <FilePickerButton integration={instance} provider={provider} />
                </Flex>
              </Box>
            </Box>
          </Stack>
        );
      })}
      <ConfirmationModal
        opened={!!datasetToDelete}
        onClose={() => setDatasetToDelete(undefined)}
        title={'Remove Linked Data Source'}
        onConfirm={() => deleteDataset({ datasetId: datasetToDelete!.id })}
        message={
          <DeleteImpactWarning
            impacts={deleteImpacts}
            itemLabel="linked data source"
            note="This will not delete the data."
          />
        }
      />
    </>
  );
};

export default ConnectedIntegration;
